import React, { useState, ChangeEvent, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faFileExport,
  faBox,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import PedidosTable, {
  createPedidosTableColumns,
  ProdutoPedido,
} from "components/tables/PedidosTable";
import ProdutosBuscaTable, {
  createProdutosBuscaTableColumns,
  ProdutoBusca,
} from "components/tables/ProdutosBuscaTable";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AxiosError } from "axios";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useBuscarProdutosPedido } from "hooks/useBuscarProdutosPedido";
import PermissionCheck from "./PermissionCheck";

const pedidosCompraBreadcrumb = [
  { label: "Compras", url: "/compras" },
  { label: "Pedidos", active: true },
];

const PedidosCompra: React.FC = () => {
  const { getQueryRetryHandler } = usePermissionModal();
  const [produtos, setProdutos] = useState<ProdutoPedido[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Query de busca com paginação infinita
  const produtosQuery = useBuscarProdutosPedido(
    debouncedSearchTerm.trim().length >= 2 ? debouncedSearchTerm : undefined
  );

  // Preparar todos os produtos de todas as páginas
  const produtosBusca = useMemo(() => {
    if (!produtosQuery.data?.pages) return [];
    return produtosQuery.data.pages.flatMap((page) =>
      page?.isSuccess ? page.data || [] : []
    );
  }, [produtosQuery.data]);

  const isSearching = produtosQuery.isLoading;
  const queryError = produtosQuery.error;
  const hasNextPage = produtosQuery.hasNextPage;
  const isFetchingNextPage = produtosQuery.isFetchingNextPage;

  // Função para remover produto
  const handleRemoverProduto = React.useCallback((id: number) => {
    // Usa setTimeout para garantir que o React processe o estado antes
    setTimeout(() => {
      setProdutos((prev) => prev.filter((p) => p.id !== id));
    }, 0);
  }, []);

  // Função para atualizar quantidade (permite 0 temporariamente para edição)
  const handleAtualizarQuantidade = React.useCallback(
    (id: number, quantidade: number) => {
      setProdutos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, quantidade } : p))
      );
    },
    []
  );

  // Função para atualizar preço de compra
  const handleAtualizarPrecoCompra = React.useCallback(
    (id: number, preco: number) => {
      setProdutos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, precoCompra: preco } : p))
      );
    },
    []
  );

  // Função para atualizar observação
  const handleAtualizarObservacao = React.useCallback(
    (id: number, observacao: string) => {
      setProdutos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, observacao } : p))
      );
    },
    []
  );

  // Função para adicionar produto da busca ao pedido
  const handleAdicionarProdutoDaBusca = React.useCallback(
    (produto: ProdutoBusca) => {
      const novoProduto: ProdutoPedido = {
        id: produto.id,
        codigo: produto.codigo.toString(),
        descricao: produto.descricao,
        quantidade: 1,
        precoCompra: produto.precoCompra || 0,
        observacao: "",
        codigosDeBarras: produto.codigosDeBarras,
        estoque: produto.estoque,
      };

      // Verifica se o produto já existe no pedido
      const produtoExiste = produtos.find((p) => p.id === produto.id);
      if (produtoExiste) {
        // Se já existe, apenas incrementa a quantidade
        setProdutos((prev) =>
          prev.map((p) =>
            p.id === produto.id ? { ...p, quantidade: p.quantidade + 1 } : p
          )
        );
      } else {
        // Se não existe, adiciona novo produto
        setProdutos((prev) => [...prev, novoProduto]);
      }

      // Limpa o campo de busca
      setSearchTerm("");
    },
    [produtos]
  );

  // Criar as colunas da tabela com as funções de callback (memoizado para evitar re-renderização)
  const columns = React.useMemo(
    () =>
      createPedidosTableColumns({
        onRemoverProduto: handleRemoverProduto,
        onAtualizarQuantidade: handleAtualizarQuantidade,
        onAtualizarPrecoCompra: handleAtualizarPrecoCompra,
        onAtualizarObservacao: handleAtualizarObservacao,
      }),
    [] // Dependências vazias porque as funções são estáveis
  );

  // Configurar o TanStack Table (sem paginação)
  const table = useReactTable({
    data: produtos,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  // Criar as colunas da tabela de busca
  const colunasBusca = React.useMemo(
    () =>
      createProdutosBuscaTableColumns({
        onAdicionarProduto: handleAdicionarProdutoDaBusca,
      }),
    [handleAdicionarProdutoDaBusca]
  );

  // Configurar o TanStack Table para a tabela de busca
  const tableBusca = useReactTable({
    data: produtosBusca,
    columns: colunasBusca,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  // Handler para scroll infinito
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      produtosQuery.fetchNextPage();
    }
  };

  // Função para exportar CSV
  const handleExportarCSV = () => {
    const csvContent = [
      [
        "Descrição",
        "Códigos de Barras",
        "Quantidade",
        "Observação",
      ],
      ...produtos.map((p) => [
        p.descricao,
        p.codigosDeBarras?.join(", ") || "",
        p.quantidade.toString(),
        p.observacao || "",
      ]),
    ]
      .map((row) => row.join(";"))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `pedido_compra_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
  };

  // Handler para busca
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Limpar todos os produtos
  const handleLimparTudo = () => {
    setProdutos([]);
  };

  return (
    <PermissionCheck error={queryError}>
      <PageBreadcrumb items={pedidosCompraBreadcrumb} />

      <div className="mb-9">      
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">Pedidos</h2>
            <h5 className="text-body-tertiary fw-semibold">
              Inclua produtos para gerar um pedido
            </h5>
          </div>
          <div className="d-flex gap-2">
            <>
              <Button
                variant="outline-danger"
                onClick={handleLimparTudo}
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Limpar Tudo
              </Button>
              <Button              
                disabled={produtos.length === 0}
                onClick={handleExportarCSV}
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                Exportar CSV
              </Button>
            </>
          </div>
        </div>

        {/* Barra de busca com dropdown de resultados */}
        <div className="mb-4 position-relative">
          <SearchBox
            className="w-100"
            placeholder="Buscar produto por código, nome ou descrição"
            onChange={handleSearchInputChange}
            value={searchTerm}
          />

          {/* Tabela de resultados da busca (dropdown absoluto) */}
          {searchTerm && (
            <Card
              className="position-absolute w-100 border shadow-lg"
              style={{
                top: "calc(100% + 0.5rem)",
                zIndex: 1050,
                maxHeight: "400px",
                overflow: "auto",
              }}
              onScroll={handleScroll}
            >
              <Card.Header className="bg-body-emphasis border-bottom py-2 px-3">
                <small className="text-body-secondary fw-semibold">
                  Resultados da Busca
                  {produtosBusca.length > 0 && (
                    <>
                      {" "}
                      - {produtosBusca.length} produto
                      {produtosBusca.length !== 1 ? "s" : ""}
                    </>
                  )}
                </small>
              </Card.Header>
              <Card.Body className="p-0">
                {isSearching && produtosBusca.length === 0 ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="text-body-secondary mt-2 mb-0 small">
                      Buscando produtos...
                    </p>
                  </div>
                ) : produtosBusca.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-body-secondary mb-0 small">
                      Nenhum produto encontrado para "{searchTerm}"
                    </p>
                  </div>
                ) : (
                  <>
                    <AdvanceTableProvider {...tableBusca}>
                      <div className="bg-body-emphasis">
                        <ProdutosBuscaTable />
                      </div>
                    </AdvanceTableProvider>

                    {/* Loading indicator quando carregando mais itens */}
                    {isFetchingNextPage && (
                      <div className="text-center py-3 border-top">
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="sm"
                        />
                        <small className="text-body-secondary ms-2">
                          Carregando mais...
                        </small>
                      </div>
                    )}

                    {/* Indicador de fim da lista */}
                    {!hasNextPage && produtosBusca.length > 20 && (
                      <div className="text-center py-2 border-top bg-body-tertiary">
                        <small className="text-body-secondary">
                          Todos os produtos foram carregados
                        </small>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </div>

        {/* Tabela de produtos */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="border-bottom-0 py-3">
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0 text-body-emphasis d-flex align-items-center">
                  <FontAwesomeIcon icon={faBox} className="me-2" />
                  Produtos no Pedido
                </h5>
              </Col>
              <Col xs="auto">
                <Badge bg="primary" pill className="fs-9 px-3 py-2">
                  {produtos.length} {produtos.length === 1 ? "item" : "itens"}
                </Badge>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {produtos.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <FontAwesomeIcon
                    icon={faBox}
                    size="4x"
                    className="text-body-tertiary opacity-50"
                  />
                </div>
                <h5 className="text-body-secondary mb-2">
                  Nenhum produto adicionado
                </h5>
                <p className="text-body-tertiary mb-0 small">
                  Use o campo de busca acima para adicionar produtos ao pedido
                </p>
              </div>
            ) : (
              <AdvanceTableProvider key={`table-${produtos.length}`} {...table}>
                <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                  <PedidosTable />
                </div>
              </AdvanceTableProvider>
            )}
          </Card.Body>
          {produtos.length > 0 && (
            <Card.Footer className="border-top">
              <Row className="align-items-center">
                <Col xs={12} md={6} className="mb-2 mb-md-0">
                  <span className="text-body-secondary">
                    Quantidade total:{" "}
                    <strong className="text-body-emphasis">
                      {produtos.reduce((sum, p) => sum + p.quantidade, 0)}
                    </strong>
                  </span>
                </Col>
                <Col xs={12} md={6} className="text-md-end">
                  <span className="text-body-secondary d-inline-flex align-items-center gap-1">
                    Valor Total Estimado{" "}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-valor-total">
                          Valor baseado no último preço comprado de cada
                          produto, considerando a quantidade a ser comprada
                        </Tooltip>
                      }
                      >
                      <span style={{ cursor: "help" }}>
                        <FontAwesomeIcon
                          icon={faCircleQuestion}
                          className="text-body-tertiary"
                          style={{ fontSize: "0.85rem" }}
                          />
                      </span>
                    </OverlayTrigger>
                          {":"}
                    <strong className="text-body-emphasis">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(
                        produtos.reduce(
                          (sum, p) => sum + (p.precoCompra || 0) * p.quantidade,
                          0
                        )
                      )}
                    </strong>
                  </span>
                </Col>
              </Row>
            </Card.Footer>
          )}
        </Card>
      </div>
    </PermissionCheck>
  );
};

export default PedidosCompra;
